import { Info } from "lucide-react";
import { type FormFactor, SPECS, pageCapacity, MAX_HOME_PAGES } from "./types";

interface Props {
  ff: FormFactor;
}

/**
 * Documents the deterministic capacity + overflow rules used by the
 * drag-and-drop logic in HomeScreenLayoutEditor.
 *
 * Keep this copy in sync with `moveItem` / `placeAt` in the editor.
 */
export function DropRulesPanel({ ff }: Props) {
  const spec = SPECS[ff];
  const cap = pageCapacity(ff);

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Info className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h4 className="text-sm font-medium">Drop rules</h4>
        <span className="ml-auto text-xs text-muted-foreground">{spec.label}</span>
      </div>

      <dl className="divide-y text-xs">
        <div className="flex items-baseline justify-between px-3 py-2">
          <dt className="text-muted-foreground">Page capacity</dt>
          <dd className="font-mono text-foreground">
            {spec.grid.cols} × {spec.grid.rows} ={" "}
            <span className="font-semibold">{cap}</span> icons
          </dd>
        </div>
        <div className="flex items-baseline justify-between px-3 py-2">
          <dt className="text-muted-foreground">Dock capacity</dt>
          <dd className="font-mono text-foreground">
            <span className="font-semibold">{spec.dockMax}</span> icons
          </dd>
        </div>
        <div className="flex items-baseline justify-between px-3 py-2">
          <dt className="text-muted-foreground">Max pages</dt>
          <dd className="font-mono text-foreground">
            <span className="font-semibold">{MAX_HOME_PAGES}</span>
          </dd>
        </div>
      </dl>

      <ul className="space-y-1.5 border-t px-3 py-2 text-xs text-muted-foreground">
        <li>
          <span className="font-medium text-foreground">Same cell:</span> dropping on
          the source cell is a no-op.
        </li>
        <li>
          <span className="font-medium text-foreground">Occupied cell:</span>{" "}
          dragging an existing icon onto another icon swaps them.
        </li>
        <li>
          <span className="font-medium text-foreground">Empty cell:</span> the icon
          is inserted at that position; following icons shift right.
        </li>
        <li>
          <span className="font-medium text-foreground">Page overflow:</span> when a
          page exceeds {cap} icons, the trailing icons spill onto the next page (a
          new page is created if needed, up to {MAX_HOME_PAGES}).
        </li>
        <li>
          <span className="font-medium text-foreground">Dock overflow:</span> the
          dock holds at most {spec.dockMax} icons. Extras spill back to the first
          page with free space.
        </li>
      </ul>
    </div>
  );
}
